/** Readable recap and verified outcome. Optional edits and requests join the main message draft. */
import { useContext, type FC } from 'react';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { SurfaceHeader } from '@/app/lib/a2ui/blocks/SurfaceHeader.tsx';
import { EditableSummary, type SummaryLine } from '@/app/lib/a2ui/blocks/EditableSummary.tsx';
import { StatusHero } from '@/app/lib/a2ui/blocks/StatusHero.tsx';
import { BlockSkeleton } from '@/app/lib/a2ui/blocks/BlockSkeleton.tsx';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import { useIntl } from 'react-intl';
import { stageFieldDraft } from '../field-draft.ts';
import { AddToMessage } from '../blocks/AddToMessage.tsx';
import { messages } from '@/app/lib/localization/messages.ts';

type SummaryStatus = 'draft' | 'committing' | 'confirmed' | 'failed';

const STATUSES: ReadonlySet<string> = new Set(['draft', 'committing', 'confirmed', 'failed']);

function isSummaryStatus(value: string): value is SummaryStatus {
  return STATUSES.has(value);
}

function readLine(raw: unknown): SummaryLine | null {
  if (!isRecord(raw)) {
    return null;
  }
  const key = str(raw.key);
  const label = str(raw.label);
  const value = str(raw.value);
  if (!key || !label) {
    return null;
  }
  return {
    key,
    label,
    value,
    caption: optStr(raw.caption),
    editable: raw.editable === true,
  };
}

function linePointer(key: string): string {
  return `/summary/${key}`;
}

const CONFIRMED_LINE_CLASS =
  'flex items-center gap-3 border-b border-border px-4.5 py-4 last:border-b-0';

/** Completed details retain the same row geometry without editing controls. */
const ConfirmedLines: FC<{ lines: SummaryLine[] }> = ({ lines }) => (
  <div className="animate-fadeUp overflow-hidden rounded-lg border border-border bg-card">
    {lines.map((line) => (
      <div key={line.key} className={CONFIRMED_LINE_CLASS}>
        <span className="w-[84px] flex-none text-sm text-muted-foreground-subtle">
          {line.label}
        </span>
        <span className="text-base font-semibold">
          {line.value}
          {line.caption ? (
            <small className="block text-sm font-normal text-muted-foreground">
              {line.caption}
            </small>
          ) : null}
        </span>
      </div>
    ))}
  </div>
);

export const SummarySurface: FC<A2uiNodeViewProps> = ({ node }) => {
  const api = useContext(A2uiSurfaceContext);
  const intl = useIntl();
  const lines = arr(node.props.lines)
    .map(readLine)
    .filter((line): line is SummaryLine => line !== null);

  if (!Array.isArray(node.props.lines) || lines.length === 0) {
    return <BlockSkeleton variant="form" />;
  }

  const statusRaw = str(node.props.status);
  const status: SummaryStatus = isSummaryStatus(statusRaw) ? statusRaw : 'draft';

  // Inline edits write back through /summary/{key}, so the agent sees the
  // corrected value in shared state on the next turn.
  const displayLines: SummaryLine[] = lines.map((line) => {
    const written = api?.getValue(linePointer(line.key));
    const value =
      status !== 'confirmed' && line.editable === true && typeof written === 'string'
        ? written
        : line.value;
    return { ...line, value };
  });

  if (status === 'confirmed') {
    return (
      <div>
        <StatusHero
          tone="success"
          title={str(
            node.props.confirmedHighlight ??
              node.props.confirmedTitle ??
              intl.formatMessage(messages.confirmed),
          )}
          subtitle={optStr(node.props.confirmedMessage ?? node.props.confirmedSubtitle)}
        />
        <ConfirmedLines lines={displayLines} />
      </div>
    );
  }

  const handleValueChange = (key: string, value: string) => {
    api?.setValue(linePointer(key), value);
    stageFieldDraft(api, {
      id: node.id,
      label:
        str(node.props.highlight ?? node.props.title) ||
        intl.formatMessage(messages.enteredDetails),
      instruction: intl.formatMessage(messages.useDetails),
      context: { component: node.component, nodeId: node.id },
      fields: lines.map((line) => ({
        id: line.key,
        label: line.label,
        value: line.value,
        path: line.editable ? linePointer(line.key) : undefined,
      })),
    });
  };

  const request = str(node.props.request ?? node.props.commitLabel);

  const title = str(node.props.highlight ?? node.props.title);

  return (
    <div>
      {title ? <SurfaceHeader title={title} /> : null}
      <EditableSummary
        lines={displayLines}
        onEdit={() => {
          // Tapping "Edit" opens the inline input (block-internal); no
          // dispatch — no edit action is declared in this contract.
        }}
        onValueChange={handleValueChange}
        editLabel={intl.formatMessage(messages.edit)}
        status={status}
        statusNote={status === 'failed' ? optStr(node.props.statusNote) : undefined}
        footnote={optStr(node.props.footnote)}
      />
      {request ? (
        <AddToMessage
          disabled={status === 'committing'}
          item={{
            id: node.id,
            label: request,
            instruction: request,
            context: { component: node.component, nodeId: node.id, action: 'commit' },
            fields: displayLines.map((line) => ({
              id: line.key,
              label: line.label,
              value: line.value,
            })),
          }}
        />
      ) : null}
    </div>
  );
};
